<template>
  <h1>{{ user.last_name }} {{ user.first_name }}</h1>

  <!-- User details -->
  <div v-if="!showEditUserDetails" class="card-body card mb-3">
    <div :style="{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }">
      <h2 class="card-title">{{ user.first_name }}'s details</h2>
      <button class="btn btn-primary btn-sm" @click="showEditUserDetails = true"><i class="fa-solid fa-pen"></i>
      </button>
    </div>
    <dl class="card-text">
      <dt>First name</dt>
      <dd>{{ user.first_name }}</dd>
      <dt>Last name</dt>
      <dd>{{ user.last_name }}</dd>
      <dt>Username</dt>
      <dd>{{ username }}</dd>
      <dt>Contact</dt>
      <dd><a :href="`mailto:${user.email}`" class="btn btn-sm btn-primary"><i class="fa-solid fa-envelope"></i>
        {{ user.email }}</a></dd>
      <dd><a :href="`tel:${user.mobile_number_international}`" class="btn btn-sm btn-primary"><i
          class="fa-solid fa-phone"></i> {{ user.mobile_number_international }}</a></dd>
      <dd><a :href="`https://wa.me/:${user.mobile_number_international}`" class="btn btn-sm btn-primary"><i
          class="fa-brands fa-whatsapp"></i> {{ user.mobile_number_international }}</a></dd>
      <dt>Roles</dt>
      <dd v-for="role in user.user_roles" :key="role">{{ role.description }}</dd>
      <dt>Favourite co-workers</dt>
      <dd>{{ user.favourite_co_workers }}</dd>
      <dt>Joined</dt>
      <dd>{{ dayjs(user.date_joined).local().format('DD-MM-YYYY hh:mm') }}</dd>
    </dl>
  </div>

  <!-- Edit User Details -->
  <div v-if="showEditUserDetails" class="mb-3">
    <EditUserDetails :user="user" @close="handleClose"/>
  </div>

  <!-- User shifts -->
  <div class="card mb-3" v-if="!showEditUserDetails">
    <div class="card-body">
      <div :style="{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }">
        <h2 class="card-title">{{ user.first_name }}'s shifts</h2>
        <button v-if="!showAddShifts" class="btn btn-primary btn-sm" @click="showAddShifts = true"><i
            class="fa-solid fa-plus"></i>
        </button>
      </div>
      <table class="table table-sm table-responsive-sm table-striped">
        <thead>
        <tr>
          <th>Shift</th>
          <th scope="col" class="d-none d-lg-table-cell">Occupation</th>
          <th scope="col" class="d-table-cell d-lg-none">n/N</th>
          <th scope="col" class="d-none d-lg-table-cell">Score</th>
          <th scope="col" class="d-table-cell d-lg-none">$</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="userShift in user_shifts" :key="userShift.id">
          <td>{{ userShift.shift.event.code }}.{{ userShift.shift.location.code }}.{{ userShift.shift.name }}</td>
          <td>{{ userShift.shift.spots_taken }}/{{ userShift.shift.capacity }}</td>
          <td>{{ userShift.shift.score }}</td>
          <td>
            <span class="d-flex align-items-center">
              <button class="btn btn-sm">
            <i v-if="userShift.status === 'pending'" class="fa-solid fa-spinner text-danger"></i>
            <i v-if="userShift.status === 'confirmed'" class="fa-regular fa-circle-check text-success"></i>
            <i v-if="userShift.status === 'waiting_list'" class="fa-solid fa-hourglass-half text-warning"></i>
            </button>
              <span class="ms-0 d-none d-lg-table-cell">{{ userShift.status_display }}</span>
            </span>
          </td>
          <td>
            <div class="d-flex flex-row gap-2">
              <!-- Confirm -->
              <button type="button" class="btn btn-sm btn-success"
                      v-if="userShift.status!=='confirmed'"
                      :disabled="userShift.shift.spots_taken >= userShift.shift.capacity"
                      @click="updateUserShiftStatus(userShift.id,'confirmed')">
                <i class="fa-solid fa-square-check"></i>
              </button>
              <!-- To pending -->
              <button type="button" class="btn btn-sm btn-secondary" v-if="userShift.status!=='pending'"
                      @click="updateUserShiftStatus(userShift.id,'pending')"><i class="fa-solid fa-circle-question"></i>
              </button>
              <!-- To waiting list -->
              <button type="button" class="btn btn-sm btn-warning" v-if="userShift.status!=='waiting_list'"
                      @click="updateUserShiftStatus(userShift.id,'waiting_list')"><i
                  class="fa-solid fa-hourglass-half"></i>
              </button>
              <button type="button" class="btn btn-sm btn-danger"
                      @click="deleteUserShift(userShift.id)"><i
                  class="fas fa-trash"></i>
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Add shifts -->
  <div class="card mb-3" v-if="showAddShifts">
    <div class="card-body">
      <div :style="{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }">
        <h2 class="card-title">Add shifts</h2>
        <button class="btn btn-primary btn-sm" @click="showAddShifts = false"><i class="fa-solid fa-eye-slash"></i>
        </button>
      </div>
      <table class="table table-sm table-responsive-sm table-striped">
        <thead>
        <tr>
          <th>Shift</th>
          <th scope="col" class="d-none d-lg-table-cell">Occupation</th>
          <th scope="col" class="d-table-cell d-lg-none">n/N</th>
          <th scope="col" class="d-none d-lg-table-cell">Score</th>
          <th scope="col" class="d-table-cell d-lg-none">$</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="shift in availableShifts" :key="shift.id">
          <td>{{ shift.event.code }}.{{ shift.location.code }}.{{ shift.name }}</td>
          <td>{{ shift.spots_taken }}/{{ shift.capacity }}</td>
          <td>{{ shift.score }}</td>
          <td>
            <div class="d-flex flex-row gap-2">
              <!-- Add -->
              <button type="button" class="btn btn-sm btn-primary"
                      @click="addUserShift(shift.id)"><i class="fa-solid fa-plus"></i>
              </button>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- User goodies -->
  <div class="card mb-3">
    <div class="card-body">
      <div :style="{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }">
        <h2 class="card-title">{{ user.first_name }}'s goodies</h2>
        <button v-if="!showAddGoodies && availableGoodies.length > 0" class="btn btn-primary btn-sm"
                @click="showAddGoodies = true"><i
            class="fa-solid fa-plus"></i></button>
      </div>
      <table v-if="user_goodies.length > 0" class="table table-sm table-responsive-sm table-striped">
        <thead>
        <tr>
          <th>Goodie</th>
          <th>Status</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="userGoodie in user_goodies" :key="userGoodie.id">
          <!-- UserGoodie -->
          <td>
            {{ userGoodie.goodie.name }}
            ({{ userGoodie.goodie.n_confirmed_user_goodies }}/{{ userGoodie.goodie.capacity }})
          </td>
          <!-- Status -->
          <td>{{ userGoodie.status_display }}</td>
          <!-- Action -->
          <td>
            <!-- Confirm -->
            <button
                v-if="userGoodie.status!=='confirmed' && userGoodie.goodie.capacity > userGoodie.goodie.n_confirmed_user_goodies"
                type="button"
                class="btn btn-sm btn-success rounded-circle me-1" title="Confirm"
                :disabled="userGoodie.goodie.capacity <= userGoodie.goodie.n_confirmed_user_goodies"><i
                class="fa-solid fa-square-check"
                @click="updateUserGoodieStatus(userGoodie.id, 'confirmed')"></i></button>
            <!-- To pending -->
            <button v-if="userGoodie.status!=='pending'" type="button"
                    class="btn btn-sm btn-secondary rounded-circle me-1" title="To pending"><i
                class="fa-solid fa-circle-question"
                @click="updateUserGoodieStatus(userGoodie.id, 'pending')"></i></button>
            <!-- To waiting list -->
            <button v-if="userGoodie.status!=='waiting_list'" type="button"
                    class="btn btn-sm btn-warning rounded-circle me-1" title="To waiting list"><i
                class="fa-solid fa-hourglass-half"
                @click="updateUserGoodieStatus(userGoodie.id, 'waiting_list')"></i></button>
            <!-- Delete -->
            <button type="button" class="btn btn-sm btn-danger rounded-circle me-1" title="Delete"><i
                class="fa-solid fa-trash"
                @click="deleteUserGoodie(userGoodie)"></i></button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Add user goodies -->
  <div class="card" v-if="showAddGoodies">
    <div class="card-body">
      <div :style="{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }">
        <h2 class="card-title">Add goodies</h2>
        <button class="btn btn-primary btn-sm" @click="showAddGoodies = false"><i class="fa-solid fa-eye-slash"></i>
        </button>
      </div>
      <table v-if="availableGoodies.length>0" class="table table-sm table-responsive-sm table-striped">
        <thead>
        <tr>
          <th>Goodie</th>
          <th>Description</th>
          <th>Action</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="goodie in availableGoodies" :key="goodie.id">
          <td>{{ goodie.name }}</td>
          <td>{{ goodie.description }}</td>
          <td>
            <div class="d-flex flex-row gap-2">
              <!-- Add -->
              <button type="button" class="btn btn-sm btn-primary"
                      @click="addUserGoodie(goodie.id)"><i class="fa-solid fa-plus"></i>
              </button>
            </div>
          </td>
        </tr>
        </tbody>

      </table>

    </div>
  </div>

</template>

<script>
import {API_BASE_URL} from "@/config";
import dayjs from "dayjs";
import EditUserDetails from "@/components/EditUserDetails.vue";
import {deleteUserShift, setUserShiftStatus} from "@/utils/userShiftUtils";
import {getCSRFToken} from "@/store/auth";
import {cancelRequestedGoodie} from "@/utils/userGoodieUtils";

export default {
  name: "OrganisationUserAdmin",
  components: {EditUserDetails},
  computed: {
    dayjs() {
      return dayjs
    }
  },
  data() {
    return {
      username: this.$route.params.username,
      user: {},
      showEditUserDetails: false,
      showAddShifts: false,
      showAddGoodies: false,
      availableShifts: [],
      organisationSlug: this.$route.params.slug,
      user_shifts: [],
      user_goodies: [],
      availableGoodies: [],
    }
  },
  mounted() {
    this.fetchUserDetails();
    this.fetchUserShifts();
    this.fetchAvailableShifts();
    this.fetchUserGoodies();
    this.fetchAvailableGoodies();
  },
  methods: {
    getCsrfToken() {
      return document.cookie.split('; ').find(row => row.startsWith('csrftoken')).split('=')[1];
    },
    async fetchUserDetails() {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${this.username}`, {
          method: 'GET',
          credentials: 'include'
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.user = await response.json();
          document.title = `${this.user.last_name} ${this.user.first_name}`;
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
      }
    },
    handleClose() {
      this.showEditUserDetails = false;
      this.fetchUserDetails();
      this.fetchUserShifts();
    },
    async fetchUserShifts() {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${this.username}/user-shifts`, {
          method: 'GET',
          credentials: 'include'
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          const data = await response.json();
          this.user_shifts = data.user_shifts;
        }
      } catch (error) {
        console.error('Error fetching user shifts:', error);
      }
    },
    async updateUserShiftStatus(userShiftId, status) {
      await setUserShiftStatus(userShiftId, status, this.getCsrfToken, this.fetchUserShifts, this.$router)
    },
    async deleteUserShift(userShiftId) {
      const confirmed = window.confirm("Are you sure you want to delete this user shift?");
      if (confirmed) {
        await deleteUserShift(userShiftId, this.getCsrfToken, this.fetchUserShifts, this.$router);
        await this.fetchAvailableShifts()
      }
    },
    async fetchAvailableShifts() {
      try {
        const response = await fetch(
            `${API_BASE_URL}/users/${this.username}/available-shifts?organisation_slug=${this.organisationSlug}`,
            {
              method: 'GET',
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.availableShifts = await response.json();
        }
      } catch (error) {
        console.error('Error fetching available shifts:', error);
      }
    },
    async addUserShift(shiftId) {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${this.username}/user-shifts`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken()
          },
          body: JSON.stringify({shift_id: shiftId})
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchUserShifts();
          await this.fetchAvailableShifts();
        }
      } catch (error) {
        console.error('Error adding user shift:', error);
      }
    },
    async fetchUserGoodies() {
      // Retrieve user goodies linked to the user
      // This method is duplicated - consider refactoring
      try {
        const response = await fetch(
            `${API_BASE_URL}/users/${this.username}/user-goodies`,
            {
              method: 'GET',
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.user_goodies = await response.json();
        }
      } catch (error) {
        console.error('Error fetching user goodies:', error);
      }
    },
    async updateUserGoodieStatus(userGoodieId, status) {
      try {
        const response = await fetch(
            `${API_BASE_URL}/user-goodies/${userGoodieId}`, {
              method: 'PATCH',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken(),
              },
              body: JSON.stringify({status}),
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchUserGoodies();
        }
      } catch (error) {
        console.error('Error updating user goodie status:', error);
      }
    },
    async deleteUserGoodie(userGoodie) {
      const confirmed = window.confirm(
          `Delete goodie ${userGoodie.goodie.name} of user ${this.user.first_name} ${this.user.last_name}?`
      );
      if (confirmed) {
        await cancelRequestedGoodie(userGoodie.id, getCSRFToken());
        await this.fetchUserGoodies();
        await this.fetchAvailableGoodies();
      }
    },
    async fetchAvailableGoodies() {
      try {
        const response = await fetch(
            `${API_BASE_URL}/users/${this.username}/available-goodies?organisation_slug=${this.organisationSlug}`,
            {
              method: 'GET',
              credentials: 'include',
            });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          this.availableGoodies = await response.json();
          if (this.availableGoodies.length === 0) {
            this.showAddGoodies = false;
          }
        }
      } catch (error) {
        console.error('Error fetching available goodies:', error);
      }
    },
    async addUserGoodie(goodieId) {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${this.username}/user-goodies`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': this.getCsrfToken()
          },
          body: JSON.stringify({goodie: goodieId})
        });
        if (response.status === 403) {
          this.$router.push({name: 'forbidden'});
        } else if (response.status === 404) {
          this.$router.push({name: 'not-found'});
        } else {
          await this.fetchUserGoodies();
          await this.fetchAvailableGoodies();
        }
      } catch (error) {
        console.error('Error adding user goodie:', error);
      }
    },
  }
}
</script>
